import {
  FormControl,
  IconButton,
  InputAdornment,
  makeStyles,
  OutlinedInput,
  Popover,
} from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp, Cancel } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { TreeInputValue, TreeItemRaw } from './Tree.types';
import TreeComponent from './TreeComponent';

interface Props {
  items: TreeItemRaw[];
  onChange?: (value: TreeInputValue) => void;
  defaultValue?: TreeInputValue;
  chunkLoading?: boolean;
  emptyLabel?: string;
  placeholder?: string;
}

const TreeInput: React.FC<Props> = React.memo(
  ({ items, onChange, defaultValue = [], chunkLoading = false, emptyLabel = '', placeholder }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState(defaultValue);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'popover' : undefined;

    useDefaultValueWatcher({ defaultValue, state, setState });

    useEffect(() => {
      if (typeof onChange === 'function') {
        onChange(state);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const onOpen = (event: React.MouseEvent<any>) => {
      setAnchorEl(event.currentTarget);
    };

    const onClose = () => {
      setAnchorEl(null);
    };

    const onResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setState([]);
      event.preventDefault();
      event.stopPropagation();
    };

    const onResetMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <>
        <FormControl fullWidth variant="outlined">
          <OutlinedInput
            className={classes.triggerBtn}
            endAdornment={
              <InputAdornment position="end">
                {state.length > 0 && (
                  <IconButton onClick={onResetClick} onMouseDown={onResetMouseDown} size="small">
                    <Cancel className={classes.resetBtn} />
                  </IconButton>
                )}
                <IconButton size="small">{!open ? <ArrowDropDown /> : <ArrowDropUp />}</IconButton>
              </InputAdornment>
            }
            readOnly={true}
            placeholder={placeholder}
            value={
              state.length > 0
                ? `${state.length} ${t('app:treeSelector:selected')}`
                : emptyLabel
                ? t(emptyLabel)
                : ''
            }
            aria-describedby={id}
            onClick={onOpen}
          />
        </FormControl>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <TreeComponent
            items={items}
            defaultValue={state}
            onCheck={setState}
            chunkLoading={chunkLoading}
          />
        </Popover>
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  triggerBtn: {
    paddingRight: theme.spacing(4),

    '&:hover': {
      cursor: 'pointer',
      background: theme.palette.common.white,
      borderColor: theme.palette.grey[700],
    },
    '& .MuiSvgIcon-root': {
      fontSize: '22px',
    },
    '& input': {
      cursor: 'pointer',
    },
  },
  resetBtn: {
    color: theme.palette.grey[400],

    '&:hover': {
      color: theme.palette.grey[500],
    },
  },
}));

export default TreeInput;
